<script setup>
import { ref, watch, onMounted } from 'vue';

const emit = defineEmits(['select', 'close']);

const search = ref('');
const searchInput = ref(null);
const products = ref([]);
const loading = ref(false);
const error = ref('');
let debounceTimer = null;

const fetchProducts = async () => {
  loading.value = true;
  error.value = '';
  try {
    const res = await fetch(
      `/agent-products?q=${encodeURIComponent(search.value.trim())}`
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    products.value = data.products || [];
  } catch (e) {
    error.value = 'Could not load products. Please try again.';
    products.value = [];
  } finally {
    loading.value = false;
  }
};

watch(search, () => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(fetchProducts, 300);
});

onMounted(() => {
  fetchProducts();
  searchInput.value?.focus();
});

const priceLabel = p => {
  const cur = p.currency || '';
  const fmt = a =>
    a == null
      ? ''
      : `${Number(a).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} ${cur}`.trim();
  const lo = fmt(p.price_min);
  const hi = fmt(p.price_max);
  if (!lo && !hi) return '';
  return lo === hi ? lo : `${lo} – ${hi}`;
};

const onSelect = p => emit('select', p);
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-modal-backdrop-light dark:bg-modal-backdrop-dark"
    @click.self="emit('close')"
  >
    <div
      class="flex flex-col w-[34rem] max-w-[92vw] max-h-[80vh] overflow-hidden border rounded-xl shadow-xl bg-n-background border-n-weak"
    >
      <div
        class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
      >
        <h3 class="text-base font-medium text-n-slate-12">Send a product card</h3>
        <button
          class="flex text-n-slate-11 hover:text-n-slate-12"
          @click="emit('close')"
        >
          <span class="i-ph-x text-lg" />
        </button>
      </div>
      <div class="p-3 border-b border-n-weak">
        <input
          ref="searchInput"
          v-model="search"
          type="text"
          placeholder="Search by name or SKU..."
          class="w-full px-3 py-2 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-brand"
        />
      </div>
      <div class="flex-1 overflow-y-auto">
        <div v-if="loading" class="p-6 text-sm text-center text-n-slate-11">
          Loading...
        </div>
        <div v-else-if="error" class="p-6 text-sm text-center text-n-ruby-11">
          {{ error }}
        </div>
        <div
          v-else-if="!products.length"
          class="p-6 text-sm text-center text-n-slate-11"
        >
          No products found.
        </div>
        <button
          v-for="p in products"
          :key="p.handle"
          class="flex items-center w-full gap-3 px-4 py-2.5 text-left border-b border-n-weak hover:bg-n-alpha-1"
          @click="onSelect(p)"
        >
          <img
            v-if="p.image"
            :src="p.image"
            alt=""
            class="flex-shrink-0 object-cover w-10 h-10 rounded-md bg-n-alpha-2"
          />
          <div v-else class="flex-shrink-0 w-10 h-10 rounded-md bg-n-alpha-2" />
          <div class="flex-1 min-w-0">
            <div class="text-sm truncate text-n-slate-12">{{ p.title }}</div>
            <div class="text-xs truncate text-n-slate-11">
              <span v-if="p.sku" class="font-mono">{{ p.sku }}</span>
              <span v-if="p.sku && priceLabel(p)"> · </span>
              <span v-if="priceLabel(p)">{{ priceLabel(p) }}</span>
            </div>
          </div>
          <span class="flex-shrink-0 i-ph-paper-plane-tilt text-n-slate-10" />
        </button>
      </div>
    </div>
  </div>
</template>
